import urlParamsMapper from 'components/data/URLParams/URLParamsMapper'
import {MiddlewareType} from 'middlewares'
import {getSearchContext} from 'modules/analytics/contexts/getSearchContext'
import {getSearchResultsContext} from 'modules/analytics/contexts/getSearchResultsContext'
import {getSearchRoomResultsContext} from 'modules/analytics/contexts/getSearchRoomResultsContext'
import {
  CLEAR_REFRESHED_STATUS,
  HISTORY_BACK,
  NAVIGATE_TO,
  NAVIGATE_TO_CONFIRMATION_PAGE,
  NAVIGATE_TO_OUTSIDE,
  NAVIGATE_TO_PAYMENT_PAGE,
  NAVIGATE_TO_ROOM_SELECTION,
  NAVIGATE_TO_SEARCH,
  navigateTo,
  NavigateToPaymentPageParams,
  PP_LOADED,
  REFRESH_PAGE,
  REFRESH_PAGE_ONCE,
  RSP_LOADED,
  SCROLL_TO,
  SCROLL_TO_WINDOW_TOP,
  UPDATE_SEARCH_PARAMS
} from 'modules/browser/actions'
import {
  setSelectedOfferCheckoutUrl,
  setSelectedRoomAndOfferPosition
} from 'modules/rooms/slice'
import {parse} from 'qs'
import {mergeDeepRight, propEq} from 'ramda'
import {
  CONFIRMATION_PAGE_PATH,
  PAYMENT_PAGE_PATH,
  ROOM_SELECTION_PAGE_PATH
} from 'routes'
import {logToDataDog} from 'services/datadogLogger'
import {initForter} from 'services/forter'
import {
  getPageAsRefreshedStatus,
  setPageAsRefreshedStatus
} from 'services/sessionStorage'
import buildUrl from 'utils/buildUrl'
import {history} from 'utils/history'
import updateHistoryLocation from 'utils/updateHistoryLocation'
import {getSearchResultsUrl} from 'utils/urls'

import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  CheckoutSearchResultsContext,
  Entity,
  Page,
  Team
} from '@daedalus/core/src/analytics/types/Events'
import {getOfferPerks} from '@daedalus/core/src/offer/business/getOfferPerks'
import {SIGN_UP_TOKEN} from '@daedalus/core/src/offer/business/privateDeals'
import {
  scrollToElementOnCenter,
  scrollToWindowTop
} from '@daedalus/core/src/utils/dom'

import {trackAll} from './analyticsV1'

const buildSearchParams = ({
  placeID,
  isAuthenticated,
  isAudience,
  params
}: {
  placeID?: string
  isAuthenticated?: boolean
  isAudience?: boolean
  params?: {
    checkIn?: string
    checkOut?: string
    rooms?: string
    optimizeRooms?: string
    utm_source?: string
    utm_campaign?: string
  }
}) => {
  // eslint-disable-next-line  new-cap
  const {searchWebsiteParams, metaParams} = urlParamsMapper()
  const authenticated = isAuthenticated ? 1 : undefined
  const token = isAudience ? SIGN_UP_TOKEN : undefined
  const {gsite} = metaParams

  const searchWebsiteParamsToBeMerged = {
    ...searchWebsiteParams,
    ...(placeID ? {placeId: placeID, hotelId: undefined} : {}),
    ...params
  }

  return mergeDeepRight(searchWebsiteParamsToBeMerged, {
    authenticated,
    token,
    gsite
  })
}

/* eslint-disable complexity */
const middleware: MiddlewareType = store => next => async action => {
  const {dispatch, getState} = store

  next(action)

  switch (action.type) {
    case HISTORY_BACK: {
      history.goBack()
      break
    }

    case REFRESH_PAGE: {
      history.go(0)
      break
    }

    case REFRESH_PAGE_ONCE: {
      if (!getPageAsRefreshedStatus()) {
        setPageAsRefreshedStatus(true)
        trackAll(`Checkout.forceRefreshPage`)
        window.setTimeout(() => {
          history.go(0)
        }, 1000)
      }

      break
    }

    case CLEAR_REFRESHED_STATUS: {
      setPageAsRefreshedStatus(false)
      break
    }

    case SCROLL_TO: {
      scrollToElementOnCenter(action.elementRef)
      break
    }

    case SCROLL_TO_WINDOW_TOP: {
      scrollToWindowTop()
      break
    }

    case NAVIGATE_TO_ROOM_SELECTION: {
      const {params} = urlParamsMapper()
      const newSearchParams = mergeDeepRight(params, {
        offerId: undefined,
        roomId: undefined,
        bookingId: undefined,
        bookingSource: undefined,
        isSplitBooking: undefined,
        roomsBundleId: undefined,
        bm: undefined,
        mbm: undefined,
        sg: undefined,
        nc: undefined,
        exp: undefined,
        pc: undefined,
        offerPerks: undefined,
        ...action?.params
      })

      dispatch(navigateTo(ROOM_SELECTION_PAGE_PATH, newSearchParams))
      break
    }

    case NAVIGATE_TO_CONFIRMATION_PAGE: {
      dispatch(
        navigateTo(
          CONFIRMATION_PAGE_PATH,
          action.params.bookingParams,
          false,
          true
        )
      )
      break
    }

    case NAVIGATE_TO_PAYMENT_PAGE: {
      try {
        const {
          urlParams,
          offerParams,
          selectedRoomPosition,
          selectedOfferPosition
        } = action.navigateParams as NavigateToPaymentPageParams

        const {offer, roomId} = offerParams

        const offerPerks = getOfferPerks(
          offer?.cancellationPenalties,
          offer?.services
        )

        const splitBookingOfferPerks = urlParams.roomsBundleId
          ? urlParams.offerPerks
          : ''

        const {replaceHistory = false} = action
        // sapi only returns checkout link
        const checkoutLink = offer?.links.find(propEq('type', 'checkout'))

        const search = checkoutLink?.href
          ? parse(new URL(checkoutLink.href).search, {
              ignoreQueryPrefix: true
            })
          : {}

        const redirectParams = {
          offerId: offer?.id,
          providerCode: offer?.providerCode,
          roomId,
          cug: offer?.cug[0],
          ...search,
          ...(urlParams.roomsBundleId ? urlParams : {}),
          ...(urlParams.roomsBundleId
            ? {offerPerks: splitBookingOfferPerks}
            : {offerPerks})
        }

        dispatch(
          setSelectedRoomAndOfferPosition({
            selectedRoomPosition,
            selectedOfferPosition
          })
        )

        dispatch(setSelectedOfferCheckoutUrl(checkoutLink?.href || null))
        dispatch(
          navigateTo(PAYMENT_PAGE_PATH, redirectParams, false, replaceHistory)
        )
      } catch (error) {
        logToDataDog({
          logName: 'NavigateToPP.Errored',
          metadata: {
            errorMessage: error,
            action: action.navigateParams
          },
          level: 'error'
        })
      }
      break
    }

    case NAVIGATE_TO_SEARCH: {
      const {
        isAuthenticated,
        isAudience,
        isNewTab,
        checkIn,
        checkOut,
        rooms,
        optimizeRooms,
        utm_source,
        utm_campaign,
        placeID
      } = action.params

      const searchParams = {
        forceCurrencyChange: 1,
        forceLanguageChange: 1,
        ...buildSearchParams({
          placeID,
          isAudience,
          isAuthenticated,
          params: {
            checkIn,
            checkOut,
            rooms,
            optimizeRooms,
            utm_source,
            utm_campaign
          }
        })
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isNewTab
        ? window.open(buildUrl(getSearchResultsUrl(), searchParams), '_blank')
        : window.location.assign(buildUrl(getSearchResultsUrl(), searchParams))

      break
    }

    case NAVIGATE_TO_OUTSIDE: {
      const {url, search} = action.params
      const {params} = urlParamsMapper()
      const newSearchParams = search ? mergeDeepRight(params, search) : null

      window.location = buildUrl(url, newSearchParams) as string & Location
      break
    }

    case NAVIGATE_TO: {
      const {url, search, replaceSearchParams, replace} = action.params
      updateHistoryLocation({url, search, replaceSearchParams, replace})
      break
    }

    case UPDATE_SEARCH_PARAMS: {
      const {search, replaceSearchParams} = action.params
      const url = history.location.pathname

      updateHistoryLocation({url, search, replaceSearchParams})
      break
    }

    case RSP_LOADED:
    case PP_LOADED: {
      if (action.type === RSP_LOADED) {
        const state = getState()

        dispatch(
          trackEvent({
            category: Category.System,
            entity: Entity.Search,
            action: Action.Succeeded,
            team: Team.Select,
            page: Page.RoomSelection,
            analyticsContext: {
              [AnalyticsContext.SearchContext]: getSearchContext(state),
              [AnalyticsContext.SearchRoomResultsContext]:
                getSearchRoomResultsContext(state, action.data?.isMobile),
              [AnalyticsContext.CheckoutSearchResultsContext]:
                getSearchResultsContext(state) as CheckoutSearchResultsContext
            }
          })
        )
      }
      initForter()

      break
    }

    default: {
      break
    }
  }
}

export default middleware
